import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, MapPin, CalendarClock, SlidersHorizontal, Check, Layers } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

const FALLBACK = ["#eb75b5", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ef4444"];

const hexToRgb = (hex) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
const rgbToHex = (r, g, b) =>
  "#" + [r, g, b].map((x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, "0")).join("");
const mix = (rgb, target, t) => rgb.map((c, i) => c + (target[i] - c) * t);
// Passt die Farbe an das Theme an: sehr dunkle Farben im Dark-Mode aufhellen,
// sehr helle Farben im Light-Mode abdunkeln → immer lesbar.
const legibleColor = (hex, isDark) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const lum = 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
  if (isDark && lum < 130) return rgbToHex(...mix(rgb, [255, 255, 255], Math.min(0.78, (130 - lum) / 150 + 0.4)));
  if (!isDark && lum > 205) return rgbToHex(...mix(rgb, [0, 0, 0], Math.min(0.55, (lum - 205) / 130 + 0.2)));
  return hex;
};

const useIsDark = () => {
  const [dark, setDark] = useState(
    () => typeof document !== "undefined" && document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setDark(el.classList.contains("dark")));
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
};

const mhdTone = (d) => {
  if (d === null || d === undefined) return { bg: "#f1f5f9", fg: "#475569" };
  if (d < 0) return { bg: "#fee2e2", fg: "#991b1b" };
  if (d <= 14) return { bg: "#fee2e2", fg: "#991b1b" };
  if (d <= 30) return { bg: "#fef3c7", fg: "#854d0e" };
  return { bg: "#d1fae5", fg: "#065f46" };
};

const nf = (v) => (v ?? 0).toLocaleString("de-DE", { maximumFractionDigits: 2 });

const ProductCard = ({ p, color, max }) => {
  const isDark = useIsDark();
  const c = legibleColor(color, isDark);
  const pct = Math.max(4, Math.min(100, max > 0 ? (p.stock / max) * 100 : 4));
  const fefo = p.fefo;
  const tone = mhdTone(fefo?.days_to_mhd);
  const low = p.min_stock > 0 && p.stock < p.min_stock;
  return (
    <Link
      to={`/artikel/${p.item_id}`}
      data-testid={`featured-card-${p.sku}`}
      className="card-hover group relative block rounded-2xl border border-[var(--border)] bg-white p-4 overflow-hidden"
      style={{ boxShadow: `inset 4px 0 0 ${c}` }}
    >
      <div
        className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-20 blur-xl transition-opacity group-hover:opacity-40"
        style={{ background: c }}
      />
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: c }} />
            <span className="font-display font-bold text-sm truncate">{p.name}</span>
          </div>
          <div className="text-[10px] font-mono text-[var(--text-muted)] mt-0.5">{p.sku}</div>
        </div>
        <span
          className="text-[9px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 shrink-0"
          style={{ background: `${c}1a`, color: c }}
        >
          {p.product_group || "—"}
        </span>
      </div>

      <div className="mt-3 flex items-end gap-1.5">
        <span className="font-display font-black text-4xl tracking-tight leading-none" style={{ color: c }}>
          {nf(p.stock)}
        </span>
        <span className="text-xs font-semibold text-[var(--text-muted)] mb-1">{p.unit}</span>
        {low && (
          <span className="ml-auto mb-1 text-[10px] font-bold text-red-600" data-testid="featured-low">
            unter Mindest
          </span>
        )}
      </div>

      <div className="mt-2 h-2 w-full rounded-full overflow-hidden" style={{ background: `${c}1a` }}>
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{ width: `${pct}%`, background: c }}
        />
      </div>

      <div className="mt-3 flex items-center gap-2 text-[11px]">
        {fefo ? (
          <>
            <span
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700"
              title={`FEFO-Charge ${fefo.charge || ""}`}
            >
              <MapPin size={11} /> {fefo.location || "kein Platz"}
            </span>
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold"
              style={{ background: tone.bg, color: tone.fg }}
            >
              <CalendarClock size={11} />
              {fefo.mhd || "—"}
              {fefo.days_to_mhd !== null && fefo.days_to_mhd !== undefined && (
                <span className="opacity-80">· {fefo.days_to_mhd}T</span>
              )}
            </span>
          </>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-500">
            <Layers size={11} /> keine freigegebene Charge
          </span>
        )}
      </div>
    </Link>
  );
};

export const FeaturedStockBoard = () => {
  const [data, setData] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sel, setSel] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/featured-stock");
      setData(data);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const openPicker = () => {
    setSel(data?.featured_ids || []);
    setPickerOpen(true);
  };
  const toggle = (id) => {
    setSel((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 6) { toast.info("Maximal 6 Produkte"); return cur; }
      return [...cur, id];
    });
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/dashboard/featured", { item_ids: sel });
      toast.success(sel.length ? `${sel.length} Produkte fixiert` : "Auf Automatik zurückgesetzt");
      setPickerOpen(false);
      await load();
    } catch (_e) { toast.error("Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  if (!data) return null;
  const products = data.products || [];
  if (products.length === 0) return null;
  const max = Math.max(...products.map((p) => p.stock), 1);

  return (
    <section className="rounded-3xl border border-[var(--border)] bg-gradient-to-br from-[var(--surface)] to-[var(--surface-alt)] p-5 md:p-6" data-testid="featured-stock-board">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="h-9 w-9 rounded-xl bg-black text-white flex items-center justify-center">
          <Boxes size={18} />
        </div>
        <div>
          <h2 className="font-display font-black text-xl leading-none text-[var(--text)]">Warenbestand · Top-Fertigware</h2>
          <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
            {data.auto ? "Automatisch nach Bestand · FEFO-Lagerplatz je Produkt" : "Manuell fixiert · FEFO-Lagerplatz je Produkt"}
          </div>
        </div>
        <button
          onClick={openPicker}
          data-testid="featured-pick-btn"
          className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-white px-3 py-1.5 text-xs font-semibold hover:border-[var(--pop-pink)] hover:text-[var(--pop-pink)] transition-colors"
        >
          <SlidersHorizontal size={13} /> Auswählen
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pop-fade">
        {products.map((p, i) => (
          <ProductCard key={p.item_id} p={p} color={p.color || FALLBACK[i % FALLBACK.length]} max={max} />
        ))}
      </div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Top-Fertigware wählen</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-[var(--text-muted)] -mt-2 mb-1">
            Wähle bis zu 6 Produkte. Leer lassen = automatisch nach Bestand. ({sel.length}/6)
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-[var(--border)]" data-testid="featured-picker-list">
            {(data.options || []).map((o) => {
              const on = sel.includes(o.id);
              return (
                <button
                  key={o.id}
                  onClick={() => toggle(o.id)}
                  data-testid={`featured-opt-${o.sku}`}
                  className={`w-full flex items-center gap-3 py-2.5 px-1 text-left transition-colors ${on ? "bg-[var(--pop-pink)]/5" : "hover:bg-slate-50"}`}
                >
                  <span className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 ${on ? "bg-[var(--pop-pink)] border-[var(--pop-pink)] text-white" : "border-[var(--border)]"}`}>
                    {on && <Check size={13} />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium truncate">{o.name}</span>
                    <span className="block text-[10px] font-mono text-[var(--text-muted)]">{o.sku}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setSel([])} data-testid="featured-reset-auto" className="rounded-full">
              Automatik
            </Button>
            <Button onClick={save} disabled={saving} data-testid="featured-save"
                    className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
              {saving ? "Speichere…" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default FeaturedStockBoard;
